import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../core/auth/auth.service';
import { TripService } from './trip.service';

// ── Data Models ───────────────────────────────────────────────────────────────

export interface TimelinePoint {
  lat: number;
  lng: number;
  timeUtc: Date;
  tripName: string;
  vesselName: string | null;
  tripIndex: number;
  totalTrips: number;
  isMine: boolean;
}

export interface ActiveBoatState {
  tripIndex: number;
  tripName: string;
  vesselName: string | null;
  isMine: boolean;
  totalTrips: number;
  lat: number;
  lng: number;
  bearing: number;
  speedKnots: number;
}

export interface ReplayPolyline {
  /** [lng, lat] pairs — MapLibre GeoJSON coordinate order */
  points: [number, number][];
  isMine: boolean;
  tripIndex: number;
}

export interface ReplayWaypoint {
  id: string;
  lat: number;
  lng: number;
  name: string;
  createTs: Date;
  /** Full API response preserved for popup/icon rendering in the map view. */
  raw?: any;
}

export interface ReplayFilterParams {
  showMine: boolean;
  showShared: boolean;
  historyDays?: number;
  customRange?: { start: Date; end: Date } | null;
}

export interface ReplayState {
  isPlaying: boolean;
  isLoading: boolean;
  hasData: boolean;
  lastLoadWasEmpty: boolean;
  currentTime: Date | null;
  startTime: Date | null;
  endTime: Date | null;
  tripProgress: number;
  tripCount: number;
  playbackSpeed: number;
  activeBoats: ActiveBoatState[];
  currentPolylines: ReplayPolyline[];
  activeWaypoints: ReplayWaypoint[];
}

interface TimeSegment {
  start: Date;
  end: Date;
}

@Injectable({ providedIn: 'root' })
export class ReplayService {

  // ── Internal State ─────────────────────────────────────────────────────────
  private _points: TimelinePoint[] = [];
  /** Pre-built index of points per trip. Rebuilt once after every load — avoids O(N) map construction on every tick. */
  private _tripPointsMap = new Map<number, TimelinePoint[]>();
  private _waypoints: ReplayWaypoint[] = [];
  private _isPlaying = false;
  private _playbackMultiplier = 1.0;
  /** 120.0 × multiplier — 1 real second ≈ 2 min trip time at 1× */
  private _playbackSpeed = 120.0;
  private _startTime: Date | null = null;
  private _endTime: Date | null = null;
  private _currentTime: Date | null = null;
  private _isLoading = false;
  private _lastLoadWasEmpty = false;
  private _filterRange: { start: Date; end: Date } | null = null;
  private _historyDays = 30;
  private _ticker: ReturnType<typeof setInterval> | null = null;
  private _activeWaypointIds = new Set<string>();

  // ── Public State Stream ────────────────────────────────────────────────────
  private _stateSubject = new BehaviorSubject<ReplayState>(this._buildState());
  readonly state$ = this._stateSubject.asObservable();

  constructor(
    private authService: AuthService,
    private tripService: TripService,
  ) {}

  // ── Simple Getters ─────────────────────────────────────────────────────────

  get isLoading() { return this._isLoading; }
  get hasData() { return this._points.length > 0; }
  get historyDays() { return this._historyDays; }
  get filterRange() { return this._filterRange; }

  consumeEmptyFlag(): void { this._lastLoadWasEmpty = false; }

  // ── Trip Segments (gaps excluded from progress) ────────────────────────────

  private get _tripSegments(): TimeSegment[] {
    if (!this._tripPointsMap.size) return [];
    const segs: TimeSegment[] = [];
    for (const pts of this._tripPointsMap.values()) {
      if (pts.length < 2) continue;
      segs.push({ start: pts[0].timeUtc, end: pts[pts.length - 1].timeUtc });
    }
    segs.sort((a, b) => a.start.getTime() - b.start.getTime());
    return segs;
  }

  /** Progress 0–1 over active trip duration only (gaps excluded). */
  get tripProgress(): number {
    if (!this._currentTime) return 0;
    const segs = this._tripSegments;
    if (!segs.length) return 0;
    let totalActiveMs = 0;
    let elapsedActiveMs = 0;
    for (const seg of segs) {
      const segDur = seg.end.getTime() - seg.start.getTime();
      totalActiveMs += segDur;
      if (this._currentTime.getTime() >= seg.end.getTime()) {
        elapsedActiveMs += segDur;
      } else if (this._currentTime.getTime() >= seg.start.getTime()) {
        elapsedActiveMs += this._currentTime.getTime() - seg.start.getTime();
      }
    }
    if (totalActiveMs <= 0) return 0;
    return Math.min(1, Math.max(0, elapsedActiveMs / totalActiveMs));
  }

  get tripCount(): number { return this._tripPointsMap.size; }

  // ── Computed: Active Boat Positions ───────────────────────────────────────

  get activeBoats(): ActiveBoatState[] {
    if (!this._tripPointsMap.size || !this._currentTime) return [];
    const ct = this._currentTime.getTime();
    const active: ActiveBoatState[] = [];

    this._tripPointsMap.forEach((pts, index) => {
      const first = pts[0];
      const last = pts[pts.length - 1];

      // Outside this trip's time window — skip
      if (ct < first.timeUtc.getTime() || ct > last.timeUtc.getTime()) return;

      let lat = last.lat, lng = last.lng;
      let bearing = 0, speedKnots = 0;

      if (ct === last.timeUtc.getTime()) {
        speedKnots = 0;
        if (pts.length >= 2) bearing = this._calcBearing(pts[pts.length - 2], last);
      } else {
        for (let i = 0; i < pts.length - 1; i++) {
          const p1 = pts[i], p2 = pts[i + 1];
          if (ct >= p1.timeUtc.getTime() && ct < p2.timeUtc.getTime()) {
            const totalMs = p2.timeUtc.getTime() - p1.timeUtc.getTime();
            const elapsedMs = ct - p1.timeUtc.getTime();
            const frac = totalMs === 0 ? 0 : Math.min(1, elapsedMs / totalMs);
            lat = p1.lat + (p2.lat - p1.lat) * frac;
            lng = p1.lng + (p2.lng - p1.lng) * frac;
            if (totalMs > 0) {
              const distM = this._haversineMeters(p1.lat, p1.lng, p2.lat, p2.lng);
              speedKnots = (distM / (totalMs / 1000)) * 1.94384;
            }
            if (p1.lat !== p2.lat || p1.lng !== p2.lng) {
              bearing = this._calcBearing(p1, p2);
            } else if (i > 0) {
              bearing = this._calcBearing(pts[i - 1], p1);
            }
            break;
          }
        }
      }

      active.push({
        tripIndex: index,
        tripName: first.tripName || 'Trip',
        vesselName: first.vesselName,
        isMine: first.isMine,
        totalTrips: first.totalTrips,
        lat, lng, bearing, speedKnots,
      });
    });
    return active;
  }

  // ── Computed: Visible Polylines ────────────────────────────────────────────

  private _computePolylines(boats: ActiveBoatState[]): ReplayPolyline[] {
    if (!this._points.length || !this._currentTime) return [];
    const ct = this._currentTime.getTime();
    const paths = new Map<number, [number, number][]>();
    const ownership = new Map<number, boolean>();

    for (const pt of this._points) {
      if (pt.timeUtc.getTime() <= ct) {
        if (!paths.has(pt.tripIndex)) paths.set(pt.tripIndex, []);
        paths.get(pt.tripIndex)!.push([pt.lng, pt.lat]);
        ownership.set(pt.tripIndex, pt.isMine);
      }
    }

    for (const boat of boats) {
      const path = paths.get(boat.tripIndex);
      if (path?.length) {
        const [lastLng, lastLat] = path[path.length - 1];
        if (lastLng !== boat.lng || lastLat !== boat.lat) {
          path.push([boat.lng, boat.lat]);
        }
      } else if (path) {
        path.push([boat.lng, boat.lat]);
      }
    }

    return Array.from(paths.entries())
      .filter(([, pts]) => pts.length > 1)
      .map(([idx, pts]) => ({ points: pts, isMine: ownership.get(idx) ?? true, tripIndex: idx }));
  }

  get currentPolylines(): ReplayPolyline[] {
    return this._computePolylines(this.activeBoats);
  }

  // ── Computed: Active Waypoints ─────────────────────────────────────────────

  get activeWaypoints(): ReplayWaypoint[] {
    if (!this._currentTime) return [];
    const ct = this._currentTime.getTime();
    return this._waypoints.filter(w => w.createTs.getTime() <= ct);
  }

  private _updateActiveWaypoints(): void {
    if (!this._currentTime) { this._activeWaypointIds = new Set(); return; }
    const ct = this._currentTime.getTime();
    const nowIds = new Set(this._waypoints.filter(w => w.createTs.getTime() <= ct).map(w => w.id));
    const changed = nowIds.size !== this._activeWaypointIds.size ||
                    ![...nowIds].every(id => this._activeWaypointIds.has(id));
    if (changed) this._activeWaypointIds = nowIds;
  }

  // ── Geo Helpers ────────────────────────────────────────────────────────────

  private _calcBearing(p1: { lat: number; lng: number }, p2: { lat: number; lng: number }): number {
    const toRad = (d: number) => d * Math.PI / 180;
    const lat1 = toRad(p1.lat), lat2 = toRad(p2.lat);
    const dLng = toRad(p2.lng - p1.lng);
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
  }

  private _haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000;
    const toRad = (d: number) => d * Math.PI / 180;
    const φ1 = toRad(lat1), φ2 = toRad(lat2);
    const Δφ = toRad(lat2 - lat1), Δλ = toRad(lng2 - lng1);
    const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // ── Playback Controls ──────────────────────────────────────────────────────

  cycleSpeed(): void {
    const m = this._playbackMultiplier;
    this._playbackMultiplier = m === 1 ? 2 : m === 2 ? 4 : m === 4 ? 8 : 1;
    this._computeDefaultSpeed();
    this._emit();
  }

  setHistoryDays(days: number): void {
    if (this._historyDays === days) return;
    this._historyDays = days;
    this._emit();
  }

  private _computeDefaultSpeed(): void {
    this._playbackSpeed = 120.0 * this._playbackMultiplier;
  }

  togglePlay(): void {
    if (!this._points.length) return;
    this._isPlaying = !this._isPlaying;

    if (this._isPlaying) {
      // Restart from beginning if at end
      if (this._currentTime && this._endTime &&
          this._currentTime.getTime() >= this._endTime.getTime()) {
        this._currentTime = this._startTime;
      }
      this._ticker = setInterval(() => {
        if (!this._currentTime || !this._endTime) return;
        const msToAdd = Math.round(16 * this._playbackSpeed);
        this._currentTime = new Date(this._currentTime.getTime() + msToAdd);
        this._fastForwardGap();
        if (this._currentTime.getTime() >= this._endTime.getTime()) {
          this._currentTime = new Date(this._endTime);
          this._isPlaying = false;
          clearInterval(this._ticker!);
          this._ticker = null;
        }
        this._updateActiveWaypoints();
        this._emit();
      }, 16);
    } else {
      this._stopTicker();
    }
    this._emit();
  }

  pause(): void {
    if (this._isPlaying) {
      this._isPlaying = false;
      this._stopTicker();
      this._emit();
    }
  }

  seekTo(target: Date): void {
    if (!this._startTime || !this._endTime) return;
    const clamped = new Date(
      Math.min(Math.max(target.getTime(), this._startTime.getTime()), this._endTime.getTime())
    );
    this._currentTime = clamped;
    this._updateActiveWaypoints();
    this._emit();
  }

  /** Seek to a fractional position (0–1) over active trip time (gaps excluded). */
  seekToProgress(progress: number): void {
    const segs = this._tripSegments;
    if (!segs.length) return;
    let totalActiveMs = 0;
    for (const seg of segs) totalActiveMs += seg.end.getTime() - seg.start.getTime();
    const targetMs = Math.round(totalActiveMs * Math.min(1, Math.max(0, progress)));
    let accumulated = 0;
    for (const seg of segs) {
      const segDur = seg.end.getTime() - seg.start.getTime();
      if (accumulated + segDur >= targetMs) {
        this.seekTo(new Date(seg.start.getTime() + (targetMs - accumulated)));
        return;
      }
      accumulated += segDur;
    }
    if (this._endTime) this.seekTo(this._endTime);
  }

  seekToTrip(targetTripIndex: number): void {
    for (const pt of this._points) {
      if (pt.tripIndex === targetTripIndex) { this.seekTo(pt.timeUtc); return; }
    }
  }

  /** If paused inside a gap between trips, jump to the next trip start. */
  private _fastForwardGap(): void {
    if (!this._tripPointsMap.size || !this._currentTime) return;
    const ct = this._currentTime.getTime();
    let anyActive = false;
    this._tripPointsMap.forEach(pts => {
      if (ct >= pts[0].timeUtc.getTime() && ct <= pts[pts.length - 1].timeUtc.getTime()) {
        anyActive = true;
      }
    });
    if (!anyActive) {
      for (const pt of this._points) {
        if (pt.timeUtc.getTime() > ct) {
          this._currentTime = new Date(pt.timeUtc);
          break;
        }
      }
    }
  }

  /** Stop timer without clearing data — map stays rendered. */
  close(): void {
    this._isPlaying = false;
    this._stopTicker();
    this._emit();
  }

  /** Fully clears all replay state — call when exiting replay mode entirely. */
  reset(): void {
    this._isPlaying = false;
    this._stopTicker();
    this._points = [];
    this._tripPointsMap.clear();
    this._waypoints = [];
    this._activeWaypointIds = new Set();
    this._currentTime = null;
    this._startTime = null;
    this._endTime = null;
    this._filterRange = null;
    this._emit();
  }

  private _stopTicker(): void {
    if (this._ticker !== null) { clearInterval(this._ticker); this._ticker = null; }
  }

  // ── GeoJSON Track Parsing (mirrors Flutter's TripTrack.fromBestAvailable) ──

  /** Normalize timestamps: space→T, append Z if no timezone present. */
  private _normalizeTsUtc(ts: string): string {
    const s = ts.trim().replace(' ', 'T');
    return (!s.endsWith('Z') && !/[+-]\d\d:?\d\d$/.test(s)) ? s + 'Z' : s;
  }

  /** Parse any date-ish string safely, handling space-delimited UTC strings. */
  private _parseDate(s: string | undefined | null): Date | null {
    if (!s) return null;
    const d = new Date(this._normalizeTsUtc(String(s)));
    return isNaN(d.getTime()) ? null : d;
  }

  /** Parse a raw GeoJSON object into {lat, lng, ts_utc?} points.
   *  Mirrors Flutter's TripTrack.fromJson exactly, including:
   *  - FeatureCollection of Point features
   *  - Feature wrapping a geometry
   *  - LineString / MultiPoint / any {coordinates:[...]} (even without a type field)
   *  - 3rd coordinate element as Unix-ms timestamp (>1e12) or ISO string
   *  - Legacy {points:[{latitude,longitude,ts_utc}]} format
   */
  private _parseGeoJsonTrack(geo: any): Array<{ lat: number; lng: number; ts_utc?: string }> {
    if (!geo) return [];
    const pts: Array<{ lat: number; lng: number; ts_utc?: string }> = [];

    // ── FeatureCollection ─────────────────────────────────────────────────────
    if (Array.isArray(geo.features)) {
      for (const f of geo.features) {
        if (!f?.geometry?.coordinates) continue;
        const c = f.geometry.coordinates;
        if (f.geometry.type === 'Point' && typeof c[0] === 'number' && typeof c[1] === 'number') {
          const ts = f.properties?.ts_utc ? this._normalizeTsUtc(f.properties.ts_utc) : undefined;
          pts.push({ lat: c[1], lng: c[0], ts_utc: ts });
        } else if (f.geometry.type === 'LineString' && Array.isArray(c)) {
          for (const coord of c) {
            if (Array.isArray(coord) && coord.length >= 2) {
              pts.push({ lat: coord[1], lng: coord[0], ts_utc: this._tsFromThird(coord[2]) });
            }
          }
        }
      }
      return pts;
    }

    // ── Feature wrapping a geometry — unwrap and recurse ──────────────────────
    if (geo.type === 'Feature' && geo.geometry) return this._parseGeoJsonTrack(geo.geometry);

    // ── LineString / MultiPoint / raw {coordinates:[...]} (any or missing type) ─
    // Flutter's TripTrack.fromJson checks `rawCoords is List` without requiring
    // a specific type string, so tracks missing the type field are still parsed.
    if (Array.isArray(geo.coordinates)) {
      for (const c of geo.coordinates) {
        if (Array.isArray(c) && c.length >= 2) {
          pts.push({ lat: c[1], lng: c[0], ts_utc: this._tsFromThird(c[2]) });
        }
      }
      return pts;
    }

    // ── Legacy format: {points:[{latitude,longitude,ts_utc}]} ─────────────────
    if (Array.isArray(geo.points)) {
      for (const p of geo.points) {
        if (typeof p.latitude === 'number' && typeof p.longitude === 'number') {
          const ts = p.ts_utc ? this._normalizeTsUtc(p.ts_utc) : undefined;
          pts.push({ lat: p.latitude, lng: p.longitude, ts_utc: ts });
        }
      }
      return pts;
    }

    return pts;
  }

  /** Extract a UTC timestamp string from a 3rd coordinate element.
   *  Mirrors Flutter: numeric > 1e12 → Unix-ms, string → ISO parse.
   */
  private _tsFromThird(third: any): string | undefined {
    if (third == null) return undefined;
    if (typeof third === 'number' && third > 1e12) {
      return new Date(third).toISOString();
    }
    if (typeof third === 'string' && third.trim()) {
      return this._normalizeTsUtc(third);
    }
    return undefined;
  }

  /** Mirror of Flutter's TripTrack.fromBestAvailable — try track_simplified first, then track. */
  private _parseTrackPoints(trip: any): Array<{ lat: number; lng: number; ts_utc?: string }> {
    const simplified = trip.track_simplified ? this._parseGeoJsonTrack(trip.track_simplified) : [];
    const regular = trip.track ? this._parseGeoJsonTrack(trip.track) : [];
    return simplified.length >= regular.length ? simplified : regular;
  }

  // ── Data Loading ───────────────────────────────────────────────────────────

  async fetchAndLoadTrips(params: {
    showMine?: boolean;
    showShared?: boolean;
    dateRange?: { start: Date; end: Date } | null;
  } = {}): Promise<void> {
    const { showMine = true, showShared = true, dateRange = null } = params;

    this._isPlaying = false;
    this._stopTicker();
    this._points = [];
    this._waypoints = [];
    this._activeWaypointIds = new Set();
    this._currentTime = null;
    this._startTime = null;
    this._endTime = null;
    this._filterRange = dateRange;
    this._lastLoadWasEmpty = false;
    this._isLoading = true;
    this._emit();

    try {
      const cutoff = dateRange?.start ?? new Date(Date.now() - this._historyDays * 86_400_000);
      const rangeEnd = dateRange?.end ?? null;
      // Stores trip raw JSON + its already-parsed points to avoid parsing GeoJSON twice
      const uniqueTrips = new Map<string, { trip: any; pts: Array<{ lat: number; lng: number; ts_utc?: string }> }>();
      // Collect both ID forms (ObjectId from profile + sub from JWT) — mirrors home-page's myUserIds
      const myUserIds = [this.authService.getUserObjectId(), this.authService.getUserIdFromToken()]
        .filter((id): id is string => !!id);

      if (showMine) {
        let nextCursor: string | null = null;
        let page = 1;
        let cursorMode = false;
        // IDs of in-range trips that had NO inline track data — will be
        // re-fetched individually as a fallback (mirrors the extra data that
        // Flutter receives because its API always returns full track objects).
        const noTrackInRange: string[] = [];

        while (true) {
          // page_size=10 matches Flutter exactly; omitFirstPage matches Flutter's
          // behaviour of NOT sending ?page=1 on the very first call.
          const resp: any = await this.tripService.getAllTrip(undefined, page, 10, nextCursor, true).toPromise();
          const results: any[] = resp?.results ?? [];
          if (!results.length) break;
          let foundOlder = false;
          for (const trip of results) {
            const start = this._parseDate(trip.start_time ?? trip.audit_data?.create_ts);
            // Use end time as anchor — a trip that started before the window but ended inside is valid
            const anchor = this._parseDate(trip.end_time) ?? start;
            if (anchor && anchor.getTime() < cutoff.getTime()) { foundOlder = true; break; }
            if (rangeEnd && start && start.getTime() > rangeEnd.getTime()) continue;
            const pts = this._parseTrackPoints(trip);
            // Mirror Flutter's TripResult.fromJson: id = json["_id"] ?? json["trip_id"]
            const tid: string = trip._id || trip.id || trip.trip_id || '';
            if (pts.length >= 2) {
              if (tid) uniqueTrips.set(tid, { trip, pts });
            } else if (tid && !uniqueTrips.has(tid)) {
              // In-range trip with no parseable inline track — queue for individual fetch
              noTrackInRange.push(tid);
            }
          }
          if (foundOlder) break;
          if (resp.next_cursor) { cursorMode = true; nextCursor = resp.next_cursor; page++; }
          else if (cursorMode) break;
          else if (resp.total_page != null && page >= resp.total_page) break;
          else if (!resp.next_cursor && resp.total_page == null) break;
          else page++;
        }

        // Fallback: fetch full trip details for in-range trips whose list response
        // omitted track data (common when the API limits response size per page).
        // Cap at 30 concurrent calls to avoid overwhelming the API.
        if (noTrackInRange.length > 0) {
          const toFetch = noTrackInRange.slice(0, 30);
          await Promise.all(toFetch.map(async (tid) => {
            if (uniqueTrips.has(tid)) return;
            try {
              const full: any = await this.tripService.getTripById(tid).toPromise();
              if (!full) return;
              const pts = this._parseTrackPoints(full);
              if (pts.length >= 2) uniqueTrips.set(tid, { trip: full, pts });
            } catch { /* trip stays excluded */ }
          }));
        }
      }

      if (showShared) {
        let nextCursor: string | null = null;
        let page = 1;
        const noTrackShared: string[] = [];

        while (page <= 20) {
          const resp: any = await this.tripService.getWatchFeedAll(page, 20, nextCursor).toPromise();
          const results: any[] = resp?.results ?? [];
          if (!results.length) break;
          let foundOlder = false;
          for (const item of results) {
            if (!item || typeof item !== 'object') continue;
            // Watch-feed items embed trip data either as trip_details OR as the item itself.
            // Also accept item.trip_id as a fallback identifier (same as Flutter's
            // TripResult.fromJson: id = json["_id"] ?? json["trip_id"]).
            const tripJson: any = (item.trip_details && typeof item.trip_details === 'object')
              ? item.trip_details
              : (item._id || item.id || item.trip_id) ? item : null;
            if (!tripJson) continue;
            const start = this._parseDate(tripJson.start_time ?? tripJson.audit_data?.create_ts);
            // Use end time as anchor — a trip that started before the window but ended inside is valid
            const anchor = this._parseDate(tripJson.end_time) ?? start;
            if (anchor && anchor.getTime() < cutoff.getTime()) { foundOlder = true; break; }
            if (rangeEnd && start && start.getTime() > rangeEnd.getTime()) continue;
            const pts = this._parseTrackPoints(tripJson);
            // Mirror Flutter's TripResult.fromJson: id = json["_id"] ?? json["trip_id"]
            const tid: string = tripJson._id || tripJson.id || tripJson.trip_id || '';
            if (pts.length >= 2) {
              if (tid) uniqueTrips.set(tid, { trip: tripJson, pts });
            } else if (tid && !uniqueTrips.has(tid)) {
              noTrackShared.push(tid);
            }
          }
          if (foundOlder) break;
          const hasMore = resp.next || resp.cursor || resp.next_cursor;
          if (!hasMore) break;
          const cursorVal = resp.cursor ?? resp.next_cursor ?? null;
          if (cursorVal) nextCursor = cursorVal;
          page++;
        }

        // Fallback: fetch full trip for shared trips that had no inline track data
        if (noTrackShared.length > 0) {
          const toFetch = noTrackShared.slice(0, 30);
          await Promise.all(toFetch.map(async (tid) => {
            if (uniqueTrips.has(tid)) return;
            try {
              const full: any = await this.tripService.getTripById(tid).toPromise();
              if (!full) return;
              const pts = this._parseTrackPoints(full);
              if (pts.length >= 2) uniqueTrips.set(tid, { trip: full, pts });
            } catch { /* trip stays excluded */ }
          }));
        }
      }

      this._lastLoadWasEmpty = uniqueTrips.size === 0;
      this._loadTrips(Array.from(uniqueTrips.values()), myUserIds);
      // Await waypoints before releasing loading state — slider must not move before waypoints are ready
      await this._fetchWaypointsForTrips(Array.from(uniqueTrips.keys()));  // keys = trip IDs
      this._isLoading = false;
      this._emit();
    } catch (e) {
      console.error('[Replay] Failed to load trips:', e);
      this._isLoading = false;
      this._emit();
    }
  }

  private _loadTrips(
    entries: Array<{ trip: any; pts: Array<{ lat: number; lng: number; ts_utc?: string }> }>,
    myUserIds: string[],
  ): void {
    this._points = [];

    // Sort chronologically before assigning sequential indices
    const sorted = [...entries].sort((a, b) => {
      const ta = this._parseDate(a.trip.start_time ?? a.trip.audit_data?.create_ts)?.getTime() ?? 0;
      const tb = this._parseDate(b.trip.start_time ?? b.trip.audit_data?.create_ts)?.getTime() ?? 0;
      return ta - tb;
    });

    let index = 1;
    for (const { trip, pts: rawPts } of sorted) {
      // Mirror home-page's isTripMine: check all creator ID fields against both user ID forms
      const creatorId = trip.audit_data?.create_user_id || trip.created_by?.identifier || trip.created_by?.user_id || '';
      const isMine = !!creatorId && myUserIds.includes(String(creatorId));

      // rawPts already parsed during fetch — no second GeoJSON parse here
      if (rawPts.length < 2) { index++; continue; }

      const tripStart = this._parseDate(trip.start_time ?? trip.audit_data?.create_ts);
      const tripEnd = this._parseDate(trip.end_time);

      // Build per-point timestamps.
      // Strategy: if ts_utc is present on track points use them directly (accurate GPS times).
      // Otherwise distribute time proportionally to cumulative distance so consecutive
      // simplified points don't produce unrealistically high instantaneous speeds.
      const pointTimes: (Date | null)[] = new Array(rawPts.length).fill(null);

      // First pass: collect real timestamps from ts_utc
      let realTsCount = 0;
      for (let i = 0; i < rawPts.length; i++) {
        if (rawPts[i].ts_utc) {
          const d = new Date(rawPts[i].ts_utc!);
          if (!isNaN(d.getTime())) { pointTimes[i] = d; realTsCount++; }
        }
      }

      if (realTsCount === 0 && tripStart && tripEnd) {
        // No per-point timestamps: interpolate by cumulative distance (constant-speed assumption).
        // This prevents the index-proportional approach from inflating speed on sparse simplified tracks.
        const cumdist: number[] = [0];
        for (let i = 0; i < rawPts.length - 1; i++) {
          cumdist.push(cumdist[i] + this._haversineMeters(
            rawPts[i].lat, rawPts[i].lng, rawPts[i + 1].lat, rawPts[i + 1].lng));
        }
        const totalDist = cumdist[cumdist.length - 1];
        const totalMs = tripEnd.getTime() - tripStart.getTime();
        for (let i = 0; i < rawPts.length; i++) {
          const frac = totalDist > 0 ? cumdist[i] / totalDist : i / Math.max(1, rawPts.length - 1);
          pointTimes[i] = new Date(tripStart.getTime() + frac * totalMs);
        }
      } else if (realTsCount < rawPts.length) {
        // Some ts_utc present: fill gaps via linear interpolation between real anchors.
        const anchors = new Map<number, Date>();
        if (tripStart) anchors.set(-1, tripStart);
        if (tripEnd) anchors.set(rawPts.length, tripEnd);
        for (let i = 0; i < rawPts.length; i++) {
          if (pointTimes[i]) anchors.set(i, pointTimes[i]!);
        }
        for (let i = 0; i < rawPts.length; i++) {
          if (pointTimes[i]) continue;
          let prevKey: number | null = null, nextKey: number | null = null;
          for (const k of anchors.keys()) {
            if (k <= i && (prevKey === null || k > prevKey)) prevKey = k;
            if (k >= i && (nextKey === null || k < nextKey)) nextKey = k;
          }
          if (prevKey === null || nextKey === null || prevKey === nextKey) continue;
          const prevTs = anchors.get(prevKey)!;
          const nextTs = anchors.get(nextKey)!;
          const span = nextTs.getTime() - prevTs.getTime();
          if (span <= 0) { pointTimes[i] = new Date(prevTs); continue; }
          const frac = (i - prevKey) / (nextKey - prevKey);
          pointTimes[i] = new Date(prevTs.getTime() + span * frac);
        }
      }

      for (let i = 0; i < rawPts.length; i++) {
        const ts = pointTimes[i];
        if (!ts) continue;
        this._points.push({
          lat: rawPts[i].lat,
          lng: rawPts[i].lng,
          timeUtc: ts,
          tripName: trip.name || 'Trip',
          vesselName: trip.vessel_name || null,
          tripIndex: index,
          totalTrips: sorted.length,
          isMine,
        });
      }
      index++;
    }

    this._points.sort((a, b) => a.timeUtc.getTime() - b.timeUtc.getTime());

    // Build the cached index once so getters don't rebuild it on every 60fps tick
    this._tripPointsMap.clear();
    for (const pt of this._points) {
      if (!this._tripPointsMap.has(pt.tripIndex)) this._tripPointsMap.set(pt.tripIndex, []);
      this._tripPointsMap.get(pt.tripIndex)!.push(pt);
    }

    if (this._points.length) {
      this._startTime = this._points[0].timeUtc;
      this._endTime = this._points[this._points.length - 1].timeUtc;
      this._currentTime = new Date(this._startTime);
      this._computeDefaultSpeed();
    }
    this._updateActiveWaypoints();
    this._emit();
  }

  private async _fetchWaypointsForTrips(tripIds: string[]): Promise<void> {
    // Fetch all trips' waypoints in parallel instead of one-by-one
    await Promise.all(tripIds.map(async (tripId) => {
      try {
        const resp: any = await this.tripService.getWaypointsByTrip(tripId).toPromise();
        const results: any[] = resp?.results ?? [];
        for (const w of results) {
          if (w.position?.latitude != null && w.position?.longitude != null && w.audit_data?.create_ts) {
            this._waypoints.push({
              id: w._id || w.id || '',
              lat: w.position.latitude,
              lng: w.position.longitude,
              name: w.name || 'Waypoint',
              createTs: this._parseDate(w.audit_data.create_ts) ?? new Date(),
              raw: w,
            });
          }
        }
      } catch (e) {
        console.error(`[Replay] Failed to load waypoints for trip ${tripId}:`, e);
      }
    }));
    // Single update after all parallel fetches complete (not N times)
    this._updateActiveWaypoints();
  }

  // ── State Emission ─────────────────────────────────────────────────────────

  private _buildState(): ReplayState {
    // Compute activeBoats once — currentPolylines needs the same result
    const activeBoats = this.activeBoats;
    return {
      isPlaying: this._isPlaying,
      isLoading: this._isLoading,
      hasData: this._points.length > 0,
      lastLoadWasEmpty: this._lastLoadWasEmpty,
      currentTime: this._currentTime ? new Date(this._currentTime) : null,
      startTime: this._startTime ? new Date(this._startTime) : null,
      endTime: this._endTime ? new Date(this._endTime) : null,
      tripProgress: this.tripProgress,
      tripCount: this.tripCount,
      playbackSpeed: this._playbackMultiplier,
      activeBoats,
      currentPolylines: this._computePolylines(activeBoats),
      activeWaypoints: this.activeWaypoints,
    };
  }

  private _emit(): void {
    this._stateSubject.next(this._buildState());
  }

  destroy(): void {
    this._stopTicker();
    this._stateSubject.complete();
  }
}
